import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Users,
  MessageSquare,
  Timer,
  Award,
  Loader2,
  BarChart3,
  Zap,
} from "lucide-react";

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendLabel, color = "brand" }) => {
  const colorClasses = {
    brand: "text-brand-600 bg-brand-50",
    green: "text-green-600 bg-green-50",
    yellow: "text-yellow-600 bg-yellow-50",
    red: "text-red-600 bg-red-50",
    blue: "text-blue-600 bg-blue-50",
    purple: "text-purple-600 bg-purple-50",
  };

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {value !== null && value !== undefined ? value : "—"}
              </span>
              {subtitle && <span className="text-sm text-gray-500">{subtitle}</span>}
            </div>
            {trend !== undefined && (
              <div className={`mt-2 flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
                <span>{Math.abs(trend)}% {trendLabel || ''}</span>
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AgentPerformanceRow = ({ agent, rank }) => {
  const getRankBadge = (rank) => {
    if (rank === 1) return <Badge className="bg-yellow-500 text-white">🥇</Badge>;
    if (rank === 2) return <Badge className="bg-gray-400 text-white">🥈</Badge>;
    if (rank === 3) return <Badge className="bg-amber-600 text-white">🥉</Badge>;
    return <Badge variant="outline">{rank}</Badge>;
  };

  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex items-center gap-3">
        {getRankBadge(rank)}
        <div>
          <p className="font-medium text-gray-900">{agent.name}</p>
          <p className="text-xs text-gray-500">{agent.tickets_handled} tickets traités</p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold text-gray-900">{agent.tickets_resolved} résolus</p>
        <p className={`text-xs ${agent.resolution_rate >= 80 ? 'text-green-600' : agent.resolution_rate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
          {agent.resolution_rate}% taux de résolution
        </p>
      </div>
    </div>
  );
};

const MiniTrendChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCreated = Math.max(...data.map(d => d.created), 1);
  
  return (
    <div className="flex items-end gap-1 h-16">
      {data.slice(-14).map((day, i) => (
        <div key={day.date} className="flex-1 flex flex-col gap-0.5" title={`${day.date}: ${day.created} créés, ${day.resolved} résolus`}>
          <div 
            className="bg-brand-500 rounded-t"
            style={{ height: `${(day.created / maxCreated) * 100}%`, minHeight: day.created > 0 ? '4px' : '0' }}
          />
          <div 
            className="bg-green-500 rounded-b"
            style={{ height: `${(day.resolved / maxCreated) * 100}%`, minHeight: day.resolved > 0 ? '4px' : '0' }}
          />
        </div>
      ))}
    </div>
  );
};

const formatMinutes = (minutes) => {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}h ${mins}m`;
};

const formatHours = (hours) => {
  if (hours === null || hours === undefined) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);
  return `${days}j ${remainingHours}h`;
};

const SupportMetricsDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/support/performance-metrics?period=${period}`);
      setMetrics(response.data);
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12 text-gray-500">
        <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Impossible de charger les métriques</p>
      </div>
    );
  }

  const { summary, live_chat, agents, trend } = metrics;

  // Determine SLA status color
  const slaColor = summary.sla_compliance_rate >= 90 ? "green" : summary.sla_compliance_rate >= 70 ? "yellow" : "red";
  
  // Determine response time status (good if < 30 min for live chat, < 4 hours for regular)
  const responseColor = summary.avg_first_response_minutes !== null 
    ? (summary.avg_first_response_minutes <= 60 ? "green" : summary.avg_first_response_minutes <= 240 ? "yellow" : "red")
    : "brand";

  return (
    <div className="space-y-6" data-testid="support-metrics-dashboard">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-brand-600" />
            Métriques de Performance
          </h3>
          <p className="text-sm text-gray-500">Analysez la performance de votre équipe de support</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="period-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 derniers jours</SelectItem>
            <SelectItem value="30d">30 derniers jours</SelectItem>
            <SelectItem value="90d">90 derniers jours</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Tickets traités"
          value={summary.total_tickets}
          subtitle="total"
          icon={MessageSquare}
          color="brand"
        />
        <MetricCard
          title="Taux de résolution"
          value={`${summary.resolution_rate}%`}
          subtitle={`${summary.resolved_tickets} résolus`}
          icon={CheckCircle}
          color={summary.resolution_rate >= 70 ? "green" : summary.resolution_rate >= 50 ? "yellow" : "red"}
        />
        <MetricCard
          title="Temps de réponse moyen"
          value={formatMinutes(summary.avg_first_response_minutes)}
          subtitle="1ère réponse"
          icon={Timer}
          color={responseColor}
        />
        <MetricCard
          title="Conformité SLA"
          value={summary.sla_compliance_rate !== null ? `${summary.sla_compliance_rate}%` : "—"}
          subtitle={`sur ${summary.sla_checked} tickets`}
          icon={Award}
          color={slaColor}
        />
      </div>

      {/* Live Chat Specific + Resolution Time */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-red-500" />
              Chat en Direct
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Total demandes</span>
                <span className="font-semibold">{live_chat.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Résolues</span>
                <span className="font-semibold text-green-600">{live_chat.resolved}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Temps de réponse</span>
                <span className={`font-semibold ${live_chat.avg_response_minutes && live_chat.avg_response_minutes <= 5 ? 'text-green-600' : live_chat.avg_response_minutes && live_chat.avg_response_minutes <= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {formatMinutes(live_chat.avg_response_minutes)}
                </span>
              </div>
              {live_chat.avg_response_minutes && live_chat.avg_response_minutes > 5 && (
                <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Objectif: moins de 5 minutes
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              Temps de Résolution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Moyenne</span>
                <span className="font-semibold">{formatHours(summary.avg_resolution_hours)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Tickets résolus</span>
                <span className="font-semibold">{summary.resolved_tickets}</span>
              </div>
              {summary.avg_resolution_hours && summary.avg_resolution_hours > 48 && (
                <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Temps de résolution élevé
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trend Chart */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              Tendance (14 jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MiniTrendChart data={trend} />
            <div className="flex justify-center gap-4 mt-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded bg-brand-500"></span>
                Créés
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded bg-green-500"></span>
                Résolus
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance */}
      {agents && agents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-brand-600" />
              Performance par Agent
            </CardTitle>
            <CardDescription>Classement basé sur le nombre de tickets traités</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {agents.slice(0, 5).map((agent, index) => (
                <AgentPerformanceRow key={agent.id} agent={agent} rank={index + 1} />
              ))}
            </div>
            {agents.length === 0 && (
              <p className="text-center text-gray-500 py-4">Aucun agent assigné</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* SLA Alerts */}
      {summary.sla_compliance_rate !== null && summary.sla_compliance_rate < 80 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-800">Alerte SLA</h4>
                <p className="text-sm text-red-700 mt-1">
                  Le taux de conformité SLA est de {summary.sla_compliance_rate}%, 
                  en dessous de l'objectif de 80%. {summary.sla_checked - Math.round(summary.sla_checked * summary.sla_compliance_rate / 100)} tickets 
                  n'ont pas été traités dans les délais.
                </p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                  <li>Vérifiez les tickets en retard</li>
                  <li>Considérez l'ajout de personnel aux heures de pointe</li>
                  <li>Améliorez les réponses automatiques du chatbot</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupportMetricsDashboard;
